"use client";

import { IUserData } from "@/types";
import React from "react";
import {
    HeadingBio,
    HeadingLocation,
    HeadingPronouns,
    HeadingTitle,
    UserImage,
} from "./heading/index";
import { cn } from "@/lib/utils";
import { BORDER_RADIUS_OPTIONS } from "./blocks/utils/block-style-classes";

interface Props {
    account: IUserData;
    theme: IUserData["chosen_theme"];
}
export function HeadingStyleBase({ account, theme }: Props) {
    return (
        <div className="relative flex shrink-0 snap-center flex-col items-center">
            <UserImage
                account={account}
                className={`size-16 md:size-28`}
                theme={theme}
            />
            <HeadingTitle
                account={account}
                className="mx-auto items-center"
                theme={theme}
            />
            <div className="flex w-fit flex-col gap-1">
                <HeadingBio
                    bio={account.bio}
                    account={account}
                    className="text-center"
                    theme={theme}
                />
                <div className="flex w-full items-center justify-between">
                    {account.settings.pronouns && (
                        <HeadingPronouns account={account} theme={theme} />
                    )}
                    {account.settings.location && (
                        <HeadingLocation account={account} theme={theme} />
                    )}
                </div>
            </div>
        </div>
    );
}
export function HeadingStyleSideways({ account, theme }: Props) {
    return (
        <div className="relative flex shrink-0 snap-center flex-col items-center">
            <div className="flex items-center gap-2 p-2">
                <UserImage
                    account={account}
                    className={`size-20 md:size-36`}
                    theme={theme}
                />
                <div>
                    <HeadingTitle
                        account={account}
                        className="justify-start text-left"
                        theme={theme}
                    />
                    <HeadingBio
                        account={account}
                        bio={account.bio}
                        className="text-left"
                        theme={theme}
                    />
                    <div className="">
                        {account.settings.pronouns && (
                            <HeadingPronouns account={account} theme={theme} />
                        )}
                        {account.settings.location && (
                            <HeadingLocation account={account} theme={theme} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function HeadingStyleBanner({ account, theme }: Props) {
    return (
        <div className="relative flex shrink-0 snap-center flex-col items-center">
            <div className="relative flex aspect-[21/9] w-full">
                <img
                    src={
                        theme.heading.image ||
                        "https://cdn.doras.to/doras/assets/fd07ecfc-cc38-4000-aca1-4d28574c9843/6db2f8a2-ff2d-42d6-8c3c-6b4b39533f3e.jpeg"
                    }
                    alt="Banner"
                    className={cn(
                        "h-full w-full object-cover",
                        BORDER_RADIUS_OPTIONS[
                            theme.profile.design.corner_radius
                        ].class
                    )}
                />
                {/* Profile picture positioned at bottom center of banner */}
                <div className="absolute -bottom-12 left-1/2 z-10 -translate-x-1/2 transform">
                    <UserImage
                        account={account}
                        className={`size-24`}
                        theme={theme}
                    />
                </div>
            </div>

            {/* Content section below banner */}
            <div className="flex flex-col items-center px-2 pt-12">
                <HeadingTitle account={account} theme={theme} />
                <div className="flex w-fit flex-col gap-1">
                    <HeadingBio
                        bio={account.bio}
                        account={account}
                        className="text-center"
                        theme={theme}
                    />
                    <div className="flex w-full items-center justify-between">
                        {account.settings.pronouns && (
                            <HeadingPronouns account={account} theme={theme} />
                        )}
                        {account.settings.location && (
                            <HeadingLocation account={account} theme={theme} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
export function HeadingStyleBanner2({ account, theme }: Props) {
    return (
        <div className="relative flex shrink-0 snap-center flex-col items-center">
            <div className="relative flex aspect-[16/9] w-full">
                <img
                    src={
                        theme.heading.image ||
                        "https://cdn.doras.to/doras/assets/fd07ecfc-cc38-4000-aca1-4d28574c9843/6db2f8a2-ff2d-42d6-8c3c-6b4b39533f3e.jpeg"
                    }
                    alt="Banner"
                    className={cn(
                        "h-full w-full object-cover",
                        BORDER_RADIUS_OPTIONS[
                            theme.profile.design.corner_radius
                        ].class
                    )}
                />
                {/* Profile picture positioned at bottom center of banner */}
                <div
                    className={cn(
                        "absolute inset-x-0 bottom-0 z-10 flex w-full transform items-center gap-2 p-3",
                        BORDER_RADIUS_OPTIONS[
                            theme.profile.design.corner_radius
                        ].class,
                        "!rounded-t-none",
                        "bg-black/30 backdrop-blur"
                    )}
                    // style={{
                    //     backgroundColor: "black",
                    //     maskImage:
                    //         "linear-gradient(to top, black 60%, transparent 100%)",
                    //     WebkitMaskImage:
                    //         "linear-gradient(to top, black 60%, transparent 100%)",
                    // }}
                >
                    <UserImage
                        account={account}
                        className={`size-28`}
                        theme={theme}
                    />
                    <div className="flex flex-col items-center justify-start px-2">
                        <HeadingTitle
                            account={account}
                            theme={theme}
                            className="justify-start text-left"
                        />
                        <div className="flex w-full flex-col gap-1">
                            <HeadingBio
                                bio={account.bio}
                                account={account}
                                className="justify-start text-left font-semibold"
                                theme={theme}
                            />
                            <div className="flex w-full min-w-full items-center gap-3">
                                {account.settings.pronouns && (
                                    <HeadingPronouns
                                        account={account}
                                        className="font-semibold"
                                        theme={theme}
                                    />
                                )}
                                {account.settings.location && (
                                    <HeadingLocation
                                        account={account}
                                        className="font-semibold"
                                        theme={theme}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content section below banner */}
        </div>
    );
}

export function HeadingStyleHeadshot({ account, theme }: Props) {
    return (
        <div className="relative flex shrink-0 snap-center flex-col items-center">
            <div className="relative flex aspect-square min-w-64 max-w-5xl flex-col px-4">
                <UserImage
                    account={account}
                    className="absolute inset-0 w-full rounded-t-xl"
                    style={{
                        maskImage:
                            "linear-gradient(to bottom, black 60%, transparent 100%)",
                        WebkitMaskImage:
                            "linear-gradient(to bottom, black 60%, transparent 100%)",
                    }}
                    theme={theme}
                />
                <div className="z-50 mt-auto flex flex-col items-center">
                    <HeadingTitle account={account} theme={theme} />
                </div>
            </div>
            <div className="flex w-fit flex-col gap-1">
                <HeadingBio
                    bio={account.bio}
                    account={account}
                    className="text-center"
                    theme={theme}
                />
                <div className="flex w-full items-center justify-between">
                    {account.settings.pronouns && (
                        <HeadingPronouns account={account} theme={theme} />
                    )}
                    {account.settings.location && (
                        <HeadingLocation account={account} theme={theme} />
                    )}
                </div>
            </div>
        </div>
    );
}
